import {
  Outlet,
  createFileRoute,
  redirect,
  useMatches,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { cachedCanSee } from "~/access/useAccess";
import AccessGate from "~/components/AccessGate";
import PageContainer from "~/components/PageContainer";
import PrivateRoutes from "~/components/PrivateRoutes";
import { MAIN_ENTRY_PAGE } from "~/utils/Constants";
import { optimisePageTitle } from "~/utils/Helper";

export const Route = createFileRoute("/_private")({
  // Pathless layout — never the leaf match, so this staticData is unused.
  staticData: { titleKey: "pageTitles.working" },
  // Nav guard on navigation within a page load, answered from the access
  // payload already cached. On a fresh load nothing is cached yet, so it fails
  // open and AccessGate holds the page until the payload lands, then applies
  // the same membership test (it also covers in-place org and role switches,
  // which don't re-run beforeLoad).
  beforeLoad: ({ matches }) => {
    const navId = matches[matches.length - 1]?.staticData?.navId;
    if (!navId) return;
    const canSee = cachedCanSee();
    if (canSee && !canSee(navId)) {
      throw redirect({ to: MAIN_ENTRY_PAGE, replace: true });
    }
  },
  component: PrivateLayout,
});

function PrivateLayout() {
  const { t } = useTranslation("common");
  const matches = useMatches();
  const { titleKey, navId, hideTitle, hideNav } =
    matches[matches.length - 1].staticData;

  return (
    <PrivateRoutes>
      <AccessGate navId={navId}>
        <PageContainer
          title={optimisePageTitle(t(titleKey))}
          hideTitle={hideTitle}
          hideNav={hideNav}
        >
          <Outlet />
        </PageContainer>
      </AccessGate>
    </PrivateRoutes>
  );
}
